import type { IChatListItem } from '@/apiClient/types/chat'

/**
 * Mirrors the backend `chat_retention_days` setting (default 30): chats whose
 * `updated_at` is older than this window are soft-deleted by the retention CronJob,
 * so the effective deletion date is `updated_at + CHAT_RETENTION_DAYS`.
 */
export const CHAT_RETENTION_DAYS = 30

/** A chat counts as "expiring soon" once this many days or fewer remain before deletion. */
export const EXPIRING_SOON_THRESHOLD_DAYS = 7

/**
 * Sidebar section labels tied to retention. Kept here (rather than inline strings) so the
 * grouping logic and the sidebar can never drift apart when checking `label === ...`.
 */
export const RECENT_LABEL = 'Recent'
export const EXPIRING_SOON_LABEL = 'Expiring soon'

const MS_PER_DAY = 24 * 60 * 60 * 1000

export interface ChatDeletionInfo {
  /** When the chat will be auto-deleted (`updated_at` + the retention window). */
  deletionDate: Date
  /**
   * Whole days remaining until deletion, rounded DOWN and never negative — so a chat
   * with under 24h left reads as `0` ("less than a day") rather than being rounded up
   * to a full day and overstating the deadline on a data-loss warning.
   */
  daysUntilDeletion: number
  /** True while the chat is within the expiring-soon window (and not already past due). */
  isExpiringSoon: boolean
}

/**
 * Derives deletion timing from `updated_at`, mirroring the backend retention CronJob
 * (soft-delete at `updated_at + CHAT_RETENTION_DAYS`). Returns `null` when there is no
 * `updated_at` to base the calculation on.
 */
export function getChatDeletionInfo(
  chat: Pick<IChatListItem, 'updated_at'>,
  now: Date = new Date(),
): ChatDeletionInfo | null {
  if (!chat.updated_at) return null

  const deletionMs = chat.updated_at * 1000 + CHAT_RETENTION_DAYS * MS_PER_DAY
  const nowMs = now.getTime()
  const msUntilDeletion = deletionMs - nowMs
  // Floor so the countdown never rounds a partial day up (a chat with 6h left shows
  // "less than a day", not "1 day"). The threshold is checked in ms — independent of the
  // display rounding — so exactly EXPIRING_SOON_THRESHOLD_DAYS still counts as expiring.
  const daysUntilDeletion = Math.max(
    0,
    Math.floor(msUntilDeletion / MS_PER_DAY),
  )
  const isExpiringSoon =
    msUntilDeletion > 0 &&
    msUntilDeletion <= EXPIRING_SOON_THRESHOLD_DAYS * MS_PER_DAY

  return {
    deletionDate: new Date(deletionMs),
    daysUntilDeletion,
    isExpiringSoon,
  }
}

/** Human phrase for the days remaining, e.g. `"3 days"`, `"1 day"`, `"less than a day"`. */
export function formatDaysUntilDeletion(days: number): string {
  if (days <= 0) return 'less than a day'
  if (days === 1) return '1 day'
  return `${days} days`
}
